import { useMemo } from 'react'
import { createEntityHooks } from '../core/createEntityHooks'
import type { BaseDocument } from '../core/types'

export interface WorkItemTypeDocument extends BaseDocument {
  teamId: string
  name: string
  icon: string
  color: string
  description: string
  sortOrder: string | null
  /** Projects this type is restricted to. Empty = global (visible to all projects). */
  projectIds: Array<string>
  isSystem: boolean
}

const hooks = createEntityHooks<WorkItemTypeDocument>('workitemtypes')

export const useWorkItemTypes = (filter?: Partial<WorkItemTypeDocument>) => {
  const { data, isLoading, db } = hooks.useEntities(filter)
  return { workItemTypes: data, isLoading, db }
}

export const useWorkItemType = (id: string | undefined) => {
  const { data, isLoading } = hooks.useEntity(id)
  return { workItemType: data, isLoading }
}

/**
 * Returns work item types available for a given project.
 * A type is available when its projectIds is empty (global) or contains the project ID.
 */
export const useWorkItemTypesForProject = (projectId: string | null | undefined) => {
  const { workItemTypes, isLoading } = useWorkItemTypes({ isDeleted: false })

  const filtered = useMemo(() => {
    if (!projectId) return workItemTypes
    return workItemTypes.filter(
      (t) => t.projectIds.length === 0 || t.projectIds.includes(projectId),
    )
  }, [workItemTypes, projectId])

  return { workItemTypes: filtered, isLoading }
}

export const useWorkItemTypeMutations = () => {
  const { create, update, delete: deleteEntity } = hooks.useMutations()
  return {
    createWorkItemType: create,
    updateWorkItemType: update,
    deleteWorkItemType: deleteEntity,
  }
}
